import React, { useState } from 'react';
import Nav from 'react-bootstrap/Nav';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import { useTenantContext } from '../components/App/AppTenant';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { AdditionalPaymentModel } from '../defs/defs';
import EqxBadge from '../shared/components/EqxBadge/EqxBadge';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxDisplayUom from '../shared/components/EqxDisplayUom/EqxDisplayUom';
import EqxError from '../shared/components/EqxError';
import EqxIcon from '../shared/components/EqxIcon';
import EqxListGroup, { EqxListGroupItem } from '../shared/components/EqxListGroup/EqxListGroup';
import EqxLoading from '../shared/components/EqxLoading';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import EqxTile, { EqxTileBlank } from '../shared/components/EqxTile/EqxTile';
import EqxTileGroup from '../shared/components/EqxTile/EqxTileGroup';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatDate } from '../shared/utils/formatDate';

// AdditionalPaymentListItem component
interface AdditionalPaymentListItemProps {
    payment: AdditionalPaymentModel;
}

const AdditionalPaymentListItem: React.FC<AdditionalPaymentListItemProps> = ({ payment }) => {
    const [open, setOpen] = useState(false);
    const hasNotes = payment.notes && payment.notes.trim().length > 0;
    const handleToggle = () => {
        if (hasNotes) {
            setOpen(o => !o);
        }
    };

    return (
        <EqxListGroupItem key={payment.id} onClick={handleToggle} style={{ cursor: hasNotes ? 'pointer' : 'default' }}>
            <div className="d-flex align-items-stretch">
                <div className="flex-grow-1">
                    <h6 className="mb-0">{payment.additionalPaymentType?.name}</h6>
                    <div className="small text-muted">
                        {payment.startDate && payment.finishDate && payment.startDate === payment.finishDate ? (
                            formatDate(payment.startDate)
                        ) : (
                            <>
                                {formatDate(payment.startDate)}
                                {payment.finishDate && ` - ${formatDate(payment.finishDate)}`}
                            </>
                        )}
                    </div>
                </div>
                <div className="text-end">
                    <div className="mb-1">
                        <strong>
                            <EqxDisplayUom
                                value={payment.quantity}
                                uom={payment.additionalPaymentType?.uom?.name || ''}
                                currencySymbol={payment.additionalPaymentType?.uom?.currencySymbol || ''}
                            />
                        </strong>
                    </div>
                    {payment.taxAmount > 0 && (
                        <div className="small text-muted">
                            Tax: <EqxDisplayUom value={payment.taxAmount} uom="GBP" currencySymbol={"£"} />
                        </div>
                    )}
                    <div className="mb-2">
                        {payment.processed ? (
                            <EqxBadge variant="success" icon="fas fa-check" text="Processed" />
                        ) : (
                            <EqxBadge variant="warning" icon="fas fa-clock" text="Pending" />
                        )}
                    </div>
                </div>
            </div>
            {hasNotes && open && (
                <EqxCard className="bg-light border-0 mb-0">
                    <strong>Notes:</strong><br />{payment.notes}
                </EqxCard>
            )}
            {hasNotes && !open && (
                <div className="text-center"><EqxIcon fa="fas fa-chevron-down" /></div>
            )}
            {!hasNotes && (
                <div className="text-center py-2"></div>
            )}
        </EqxListGroupItem>
    );
};

// Main content
const AdditionalPaymentsContent: React.FC = () => {
    const api = useApi();
    const [tab, setTab] = useState<'pending' | 'processed'>('pending');
    const tenant = useTenantContext();

    const pendingRequest = useApiEndpoint(api, api.endpoints.additionalPaymentsPending());
    const processedRequest = useApiEndpoint(api, api.endpoints.additionalPaymentsProcessed());

    const currentRequest = tab === 'pending' ? pendingRequest : processedRequest;
    const { loading, error, response: payments } = currentRequest;

    const reset = () => {
        pendingRequest.reset();
        processedRequest.reset();
    };

    const { isOffline } = useOffline()

    // Check allowed
    const mainContext = useMainLayoutContext()
    if (!mainContext.permissions?.canAddExpenses) {
        return <EqxError error="You do not have permission to view expenses." />
    }
    if (!tenant.isManagedPayroll) {
        return <EqxError error="Managed Payroll is not enabled for this business" />;
    }
    if (!mainContext.user?.staffID) {
        return <EqxError error="Only staff can view expenses" />;
    }

    return (
        <>
            <Nav variant="pills" activeKey={tab} onSelect={k => { reset(); setTab(k as 'pending' | 'processed') }} className="mb-3 justify-content-end">
                <Nav.Item>
                    <Nav.Link eventKey="pending">Pending</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="processed">Processed</Nav.Link>
                </Nav.Item>
            </Nav>
            <EqxRequestHandler
                state={{ loading, error }}
                isEmpty={!payments || payments.length === 0}
                empty={(
                    <EqxCard isSection className="p-5 text-center text-muted">
                        <EqxIcon fa="fas fa-plus-circle" /> No expenses found
                    </EqxCard>
                )}
                loading={(
                    <EqxCard isSection className="p-5 text-center text-muted">
                        <EqxLoading />
                    </EqxCard>
                )}
            >
                <EqxCard isSection noPadding>
                    <EqxListGroup isChild>
                        {payments && payments.map((payment: AdditionalPaymentModel) => (
                            <AdditionalPaymentListItem key={payment.id} payment={payment} />
                        ))}
                    </EqxListGroup>
                </EqxCard>
            </EqxRequestHandler>
            <EqxTileGroup className='mt-3' size="xs">
                <EqxTile
                    className='col-6'
                    title="Create Expense"
                    icon="fas fa-plus-circle"
                    color="success"
                    link="/expenses/create"
                    disabled={isOffline}
                />
                <EqxTileBlank className='col-6' />
            </EqxTileGroup>
        </>
    );
};

const PageAdditionalPayments: React.FC = () => {

    return (
        <MainLayout pageTitle="Expenses">
            <AdditionalPaymentsContent />
        </MainLayout>
    );
};

export default PageAdditionalPayments; 